// src/pages/purchaseOrder/PurchaseOrderListPage.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Package,
  Plus,
  Eye,
  Edit,
  CheckCircle,
  Truck,
  Calendar,
  Download,
  FileDown,
  Sheet,
  PackagePlus,
  Undo2,
  CreditCard,
  User,
  Phone,
  Warehouse,
  Clock,
  XCircle,
  PackageCheck,
  FileText,
  Wallet
} from 'lucide-react';
import {
  PageHeader,
  DataTable,
  FilterPanel,
  PrintPreviewModal
} from '../../components/common';
import {
  getAllPurchaseOrders,
  getUserWarehouses,
  getPurchaseOrderById
} from '../../services/api/purchaseOrder';
import { receiveEntirePurchaseOrder } from '../../services/api/procurement/grn';
import { exportPurchaseOrderExcel } from '../../utils/purchaseOrderExport';
import { exportPurchaseOrderPDF } from '../../utils/procurementPdfExporter';
import { getAllSuppliers } from '../../services/api/supplier';
import { usePermissions } from '../../hooks/usePermissions';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { formatCurrency } from '../../utils/orderCalculations';
import { formatDate, formatTime } from '../../utils/dateUtils';
import { getCssVar } from '../../utils/themeColors';
import { buildProcurementCreatePath, buildReturnState } from '../../utils/procurementNavigation';
import PurchaseOrderA4Document from './PurchaseOrderA4Document';
import PurchaseOrderPaymentRecordsModal from './components/PurchaseOrderPaymentRecordsModal';
import PurchaseOrderDetailsModal from './components/PurchaseOrderDetailsModal';
import ActionMenu from '../../components/ui/ActionMenu/ActionMenu';
import DocNumberCell from '../procurement/components/DocNumberCell';
import tb from '../procurement/components/ProcurementTable.module.css';
import styles from './PurchaseOrderListPage.module.css';

const STATUS_ICONS = {
  draft: FileText,
  pending: Clock,
  partial: PackagePlus,
  received: PackageCheck,
  cancelled: XCircle,
};

const PAYMENT_STATUS_ICONS = {
  unpaid: Clock,
  partial: Wallet,
  paid: CheckCircle,
};

const PAYMENT_STATUS_CONFIG = {
  unpaid: { color: '#ef4444', bgColor: '#fee2e2' },
  partial: { color: '#f59e0b', bgColor: '#fef3c7' },
  paid: { color: '#10b981', bgColor: '#d1fae5' },
};

const PurchaseOrderListPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { t } = useLocalization();
  const { success, error: showError } = useAlert();

  // State management
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    total: 0,
    pageSizeOptions: [20, 50, 100]
  });

  // Sorting state
  const [sorting, setSorting] = useState({
    orderBy: 'createdAt',
    orderDirection: 'DESC'
  });

  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    warehouseId: '',
    supplierId: '',
    status: '',
    dateFrom: '',
    dateTo: ''
  });

  // Reference data for filters
  const [referenceData, setReferenceData] = useState({
    warehouses: [],
    suppliers: []
  });

  // Receive dialog
  const [showReceiveDialog, setShowReceiveDialog] = useState(false);
  const [receiveTarget, setReceiveTarget] = useState(null);
  const [isReceiving, setIsReceiving] = useState(false);

  // Download state: tracks `${id}-pdf` or `${id}-excel`
  const [downloadingKey, setDownloadingKey] = useState(null);

  // Print preview modal (A4 only, mirrors the GRN list's precedent for a bulk/quick download flow)
  const [printOrder, setPrintOrder] = useState(null);
  const [paymentTarget, setPaymentTarget] = useState(null);
  const [detailsOrder, setDetailsOrder] = useState(null);

  // Status configuration
  const statusConfig = {
    draft: {
      label: t('purchaseOrder.statusDraft'),
      color: '#6b7280',
      bgColor: '#f3f4f6'
    },
    pending: {
      label: t('purchaseOrder.statusPending'),
      color: '#f59e0b',
      bgColor: '#fef3c7'
    },
    received: {
      label: t('purchaseOrder.statusReceived'),
      color: '#10b981',
      bgColor: '#d1fae5'
    },
    partial: {
      label: t('purchaseOrder.statusPartial'),
      color: '#3b82f6',
      bgColor: '#dbeafe'
    },
    cancelled: {
      label: t('purchaseOrder.statusCancelled'),
      color: '#ef4444',
      bgColor: '#fee2e2'
    }
  };

  // Load reference data on mount
  useEffect(() => {
    loadReferenceData();
  }, []);

  // Load purchase orders when filters or pagination/sorting changes
  useEffect(() => {
    loadPurchaseOrders();
  }, [pagination.currentPage, pagination.pageSize, sorting.orderBy, sorting.orderDirection, filters]);

  const loadReferenceData = async () => {
    try {
      const [warehousesRes, suppliersRes] = await Promise.all([
        getUserWarehouses(),
        getAllSuppliers({ limit: 1000, isActive: true })
      ]);

      setReferenceData({
        warehouses: warehousesRes.warehouses || [],
        suppliers: suppliersRes.suppliers || []
      });
    } catch (error) {
      console.error('Failed to load reference data:', error);
      showError(t('purchaseOrder.failedToLoadData'));
    }
  };

  const loadPurchaseOrders = async () => {
    try {
      setIsLoading(true);
      const params = {
        page: pagination.currentPage,
        limit: pagination.pageSize,
        orderBy: sorting.orderBy,
        orderDirection: sorting.orderDirection
      };

      // Add filters
      if (filters.search) params.search = filters.search;
      if (filters.warehouseId) params.warehouseId = filters.warehouseId;
      if (filters.supplierId) params.supplierId = filters.supplierId;
      if (filters.status) params.status = filters.status;
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;

      const response = await getAllPurchaseOrders(params);
      setPurchaseOrders(response.purchaseOrders || []);
      setPagination(prev => ({
        ...prev,
        total: response.total || 0
      }));
    } catch (error) {
      console.error('Failed to load purchase orders:', error);
      if (error.response?.status === 403) {
        showError(t('errors.permissionDenied'));
      } else {
        showError(t('purchaseOrder.failedToLoadData'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleSearchChange = (searchValue) => {
    setFilters(prev => ({ ...prev, search: searchValue }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination(prev => ({
      ...prev,
      pageSize: newPageSize,
      currentPage: 1
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      warehouseId: '',
      supplierId: '',
      status: '',
      dateFrom: '',
      dateTo: ''
    });
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleReceive = async () => {
    if (!receiveTarget) return;
    try {
      setIsReceiving(true);
      await receiveEntirePurchaseOrder(receiveTarget.id);
      success(t('purchaseOrder.purchaseOrderReceived', { orderNumber: receiveTarget.purchaseOrderNumber }), {
        title: t('common.success'),
        duration: 4000
      });
      setShowReceiveDialog(false);
      setReceiveTarget(null);
      loadPurchaseOrders();
    } catch (err) {
      console.error('Failed to receive purchase order:', err);
      showError(err.response?.data?.message || t('purchaseOrder.failedToReceive'), {
        title: t('common.error'),
        duration: 6000
      });
    } finally {
      setIsReceiving(false);
    }
  };

  const handleOpenPrintPreview = async (row) => {
    const key = `${row.id}-pdf`;
    if (downloadingKey === key) return;
    try {
      setDownloadingKey(key);
      const response = await getPurchaseOrderById(row.id);
      setPrintOrder(response.purchaseOrder);
    } catch (err) {
      console.error('Failed to load purchase order:', err);
      showError(t('purchaseOrder.failedToLoadData'));
    } finally {
      setDownloadingKey(null);
    }
  };

  const handleOpenDetails = async (row) => {
    const key = `${row.id}-view`;
    if (downloadingKey === key) return;
    try {
      setDownloadingKey(key);
      const response = await getPurchaseOrderById(row.id);
      setDetailsOrder(response.purchaseOrder);
    } catch (err) {
      console.error('Failed to load purchase order:', err);
      showError(t('purchaseOrder.failedToLoadData'));
    } finally {
      setDownloadingKey(null);
    }
  };

  const handleDownloadExcel = async (row) => {
    const key = `${row.id}-excel`;
    if (downloadingKey === key) return;
    try {
      setDownloadingKey(key);
      const response = await getPurchaseOrderById(row.id);
      await exportPurchaseOrderExcel(response.purchaseOrder);
    } catch (err) {
      console.error('Failed to download Excel:', err);
      showError(t('purchaseOrder.failedToLoadData'));
    } finally {
      setDownloadingKey(null);
    }
  };

  const hasActiveFilters = () => {
    return Object.values(filters).some(value => value !== '');
  };

  const handleExport = () => {
    const headers = [
      t('purchaseOrder.poNumber'),
      t('purchaseOrder.supplier'),
      t('purchaseOrder.warehouse'),
      t('purchaseOrder.orderDate'),
      t('purchaseOrder.expectedDeliveryDate'),
      t('purchaseOrder.status'),
      t('purchaseOrder.grandTotal'),
      t('purchaseOrder.totalItems'),
      t('common.createdBy'),
      t('common.createdDate')
    ];
    
    const rows = purchaseOrders.map(po => [
      po.purchaseOrderNumber,
      po.Supplier?.name || t('common.na'),
      po.Warehouse?.name || t('common.na'),
      formatDate(po.orderDate),
      po.expectedDeliveryDate ? formatDate(po.expectedDeliveryDate) : t('common.na'),
      statusConfig[po.status]?.label || po.status,
      po.grandTotal,
      po.totalItems,
      po.createdBy?.name || t('common.na'),
      formatDate(po.createdAt)
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `purchase-orders-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const canEdit = (order) => {
    return !['received', 'cancelled'].includes(order.status) && !order.hasActiveReceipts;
  };

  const canReceive = (order) => {
    return ['pending', 'partial'].includes(order.status);
  };

  const canCreateGrn = (order) => {
    return ['pending', 'partial'].includes(order.status);
  };

  const canCreateRtv = (order) => {
    return ['received', 'partial'].includes(order.status);
  };

  const canRecordPayment = (order) => {
    return !['draft', 'cancelled'].includes(order.status);
  };

  // Define filter configuration
  const filterDefinitions = [
    {
      key: 'warehouseId',
      label: t('purchaseOrder.warehouse'),
      type: 'select',
      icon: <Truck size={18} />,
      placeholder: t('purchaseOrder.allWarehouses'),
      options: referenceData.warehouses.map(w => ({
        value: w.id.toString(),
        label: w.name
      }))
    },
    {
      key: 'supplierId',
      label: t('purchaseOrder.supplier'),
      type: 'select',
      placeholder: t('purchaseOrder.allSuppliers'),
      options: referenceData.suppliers.map(s => ({
        value: s.id.toString(),
        label: s.name
      }))
    },
    {
      key: 'status',
      label: t('common.status'),
      type: 'select',
      placeholder: t('purchaseOrder.allStatuses'),
      options: Object.entries(statusConfig).map(([key, config]) => ({
        value: key,
        label: config.label
      }))
    },
    {
      key: 'date',
      label: t('purchaseOrder.dateRange'),
      type: 'daterange',
      gridColumn: 'span 2'
    }
  ];

  // Define table columns
  const columns = [
    {
      key: 'purchaseOrderNumber',
      header: t('purchaseOrder.poNumber'),
      sortable: true,
      width: '150px',
      render: (row) => (
        <DocNumberCell number={row.purchaseOrderNumber} to={`/inventory/purchase-orders/${row.id}`} />
      )
    },
    {
      key: 'supplier',
      header: t('purchaseOrder.supplier'),
      width: '200px',
      render: (row) => (
        <div className={tb.partyInfo}>
          <div className={tb.partyName}><User size={14} />{row.Supplier?.name || t('common.na')}</div>
          {row.Supplier?.contactPerson && (
            <div className={tb.partyMeta}><User size={12} />{row.Supplier.contactPerson}</div>
          )}
          {row.Supplier?.phone && (
            <div className={tb.partyMeta}><Phone size={12} />{row.Supplier.phone}</div>
          )}
        </div>
      )
    },
    {
      key: 'warehouse',
      header: t('purchaseOrder.warehouse'),
      width: '150px',
      render: (row) => (
        <div className={tb.shopBadge}><Warehouse size={12} />{row.Warehouse?.name || t('common.na')}</div>
      )
    },
    {
      key: 'orderDate',
      header: t('purchaseOrder.orderDate'),
      sortable: true,
      width: '120px',
      render: (row) => (
        <div className={tb.dateInfo}><Calendar size={12} />{formatDate(row.orderDate)}</div>
      )
    },
    {
      key: 'expectedDeliveryDate',
      header: t('purchaseOrder.expectedDeliveryDate'),
      width: '140px',
      render: (row) => (
        row.expectedDeliveryDate
          ? <div className={tb.dateInfo}><Truck size={12} />{formatDate(row.expectedDeliveryDate)}</div>
          : <span className={tb.naText}>—</span>
      )
    },
    {
      key: 'status',
      header: t('purchaseOrder.status'),
      width: '140px',
      render: (row) => {
        const statusInfo = statusConfig[row.status] || statusConfig.draft;
        const StatusIcon = STATUS_ICONS[row.status] || Clock;
        return (
          <div
            className={tb.statusBadge}
            style={{
              backgroundColor: statusInfo.bgColor,
              borderColor: statusInfo.color,
              color: statusInfo.color
            }}
          >
            <StatusIcon size={14} />
            <span>{statusInfo.label}</span>
          </div>
        );
      }
    },
    {
      key: 'paymentStatus',
      header: t('purchaseOrder.paymentStatus'),
      width: '110px',
      render: (row) => {
        const info = PAYMENT_STATUS_CONFIG[row.paymentStatus] || PAYMENT_STATUS_CONFIG.unpaid;
        const PayIcon = PAYMENT_STATUS_ICONS[row.paymentStatus] || Clock;
        return (
          <div
            className={tb.statusBadge}
            style={{ backgroundColor: info.bgColor, borderColor: info.color, color: info.color }}
          >
            <PayIcon size={14} />
            <span>{t(`purchaseOrder.paymentStatus_${row.paymentStatus || 'unpaid'}`)}</span>
          </div>
        );
      }
    },
    {
      key: 'grandTotal',
      header: t('purchaseOrder.grandTotal'),
      sortable: true,
      width: '130px',
      align: 'right',
      render: (row) => (
        <div className={tb.totalAmount}>{formatCurrency(row.grandTotal)}</div>
      )
    },
    {
      key: 'totalItems',
      header: t('purchaseOrder.totalItems'),
      width: '100px',
      align: 'center',
      render: (row) => (
        <div className={styles.itemsCount}>
          <Package size={12} />
          {row.totalItems || 0}
        </div>
      )
    },
    {
      key: 'createdAt',
      header: t('common.created'),
      sortable: true,
      width: '140px',
      render: (row) => (
        <div className={tb.dateCell}>
          <div className={tb.dateInfo}><Calendar size={12} />{formatDate(row.createdAt)}</div>
          <div className={tb.timeInfo}>{formatTime(row.createdAt)}</div>
        </div>
      )
    }
  ];

  // Define table actions (more than 3 → overflow "more" menu via ActionMenu)
  const tableActions = {
    render: (row) => (
      <ActionMenu
        maxVisible={3}
        className={tb.actionsWrapper}
        triggerClassName={`${tb.actionButton} ${tb.viewButton}`}
        actions={[
          {
            key: 'view',
            icon: Eye,
            label: t('common.view'),
            onClick: () => handleOpenDetails(row),
            className: `${tb.actionButton} ${tb.viewButton}`,
            disabled: downloadingKey === `${row.id}-view`
          },
          {
            key: 'edit',
            icon: Edit,
            label: t('common.edit'),
            to: `/inventory/purchase-orders/${row.id}/edit`,
            className: `${tb.actionButton} ${tb.editButton}`,
            hidden: !(canEdit(row) && hasPermission('edit_purchase_order'))
          },
          {
            key: 'payments',
            icon: CreditCard,
            label: t('purchaseOrder.payments'),
            onClick: () => setPaymentTarget(row),
            className: `${tb.actionButton} ${tb.actionSuccess}`,
            hidden: !(canRecordPayment(row) && hasPermission('view_supplier_payment'))
          },
          {
            key: 'receive',
            icon: CheckCircle,
            label: t('purchaseOrder.receiveOrder'),
            onClick: () => { setReceiveTarget(row); setShowReceiveDialog(true); },
            className: `${tb.actionButton} ${tb.actionSuccess}`,
            hidden: !(canReceive(row) && hasPermission('receive_purchase_order'))
          },
          {
            key: 'grn',
            icon: PackagePlus,
            label: t('purchaseOrder.createGoodsReceivedNote'),
            onClick: () => navigate(
              buildProcurementCreatePath('/procurement/grn/create', { purchaseOrderId: row.id }, '/inventory/purchase-orders', t('purchaseOrder.backToPurchaseOrders')),
              { state: buildReturnState('/inventory/purchase-orders', t('purchaseOrder.backToPurchaseOrders')) }
            ),
            className: `${tb.actionButton} ${tb.actionSuccess}`,
            hidden: !(canCreateGrn(row) && hasPermission('create_grn'))
          },
          {
            key: 'rtv',
            icon: Undo2,
            label: t('purchaseOrder.createReturnToVendor'),
            onClick: () => navigate(
              buildProcurementCreatePath('/procurement/return-to-vendor/create', { purchaseOrderId: row.id }, '/inventory/purchase-orders', t('purchaseOrder.backToPurchaseOrders')),
              { state: buildReturnState('/inventory/purchase-orders', t('purchaseOrder.backToPurchaseOrders')) }
            ),
            className: `${tb.actionButton} ${tb.actionWarning}`,
            hidden: !(canCreateRtv(row) && hasPermission('create_rtv'))
          },
          {
            key: 'pdf',
            icon: FileDown,
            label: t('purchaseOrder.downloadPdf'),
            onClick: () => handleOpenPrintPreview(row),
            className: `${tb.actionButton} ${styles.pdfButton}`,
            disabled: downloadingKey === `${row.id}-pdf`
          },
          {
            key: 'excel',
            icon: Sheet,
            label: t('purchaseOrder.downloadExcel'),
            onClick: () => handleDownloadExcel(row),
            className: `${tb.actionButton} ${styles.excelButton}`,
            disabled: downloadingKey === `${row.id}-excel`
          }
        ]}
      />
    ),
    width: '130px',
    frozen: true
  };

  return (
    <div className={styles.purchaseOrderListPage}>
      {/* Page Header */}
      <PageHeader
        title={t('purchaseOrder.allPurchaseOrders')}
        description={t('purchaseOrder.manageAndTrackPurchaseOrders')}
        icon={Package}
        actions={
          hasPermission('create_purchase_order') && (
            <Link to="/inventory/purchase-orders/create" className={styles.primaryButton}>
              <Plus size={20} />
              {t('purchaseOrder.createPurchaseOrder')}
            </Link>
          )
        }
      />

      {/* Search and Filters Row */}
      <FilterPanel
        filters={filterDefinitions}
        values={filters}
        onChange={handleFilterChange}
        onClear={handleClearFilters}
        show={showFilters}
        onToggle={() => setShowFilters(!showFilters)}
        onExport={handleExport}
        showExport={true}
        exportButtonText={t('common.export')}
        searchConfig={{
          show: true,
          value: filters.search,
          onChange: handleSearchChange,
          placeholder: t('purchaseOrder.searchByPoNumber')
        }}
        pageSizeConfig={{
          show: true,
          value: pagination.pageSize,
          onChange: handlePageSizeChange,
          options: pagination.pageSizeOptions
        }}
      />

      {/* Data Table */}
      <DataTable
        data={purchaseOrders}
        columns={columns}
        actions={tableActions}
        onRowClick={(row) => navigate(`/inventory/purchase-orders/${row.id}`)}
        sorting={sorting}
        onSortChange={setSorting}
        pagination={pagination}
        onPaginationChange={setPagination}
        loading={isLoading}
        emptyMessage={
          hasActiveFilters() 
            ? t('purchaseOrder.noPurchaseOrdersFound')
            : t('purchaseOrder.noPurchaseOrdersYet')
        }
        emptyDescription={
          hasActiveFilters()
            ? t('purchaseOrder.noPurchaseOrdersFoundDescription')
            : t('purchaseOrder.noPurchaseOrdersYetDescription')
        }
        emptyIcon={<Package size={64} />}
        emptyAction={
          hasActiveFilters() ? (
            <button onClick={handleClearFilters} className={styles.primaryButton}>
              {t('common.clearFilters')}
            </button>
          ) : hasPermission('create_purchase_order') ? (
            <Link to="/inventory/purchase-orders/create" className={styles.primaryButton}>
              <Plus size={18} />
              {t('purchaseOrder.createFirstPurchaseOrder')}
            </Link>
          ) : null
        }
      />
      {/* Receive Dialog */}
      {showReceiveDialog && receiveTarget && (
        <div className={styles.dialogOverlay}>
          <div className={styles.dialogContent}>
            <div className={styles.dialogHeader}>
              <CheckCircle className={styles.dialogIconSuccess} size={24} />
              <h3>{t('purchaseOrder.confirmReceiveTitle')}</h3>
            </div>
            <div className={styles.dialogBody}>
              <p>{t('purchaseOrder.confirmReceive')}</p>
              <p><strong>{receiveTarget.purchaseOrderNumber}</strong></p>
            </div>
            <div className={styles.dialogActions}>
              <button
                onClick={() => { setShowReceiveDialog(false); setReceiveTarget(null); }}
                className={styles.dialogCancelButton}
                disabled={isReceiving}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleReceive}
                className={styles.dialogReceiveButton}
                disabled={isReceiving}
              >
                {isReceiving ? t('purchaseOrder.receivingOrder') : t('common.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {printOrder && (
        <PrintPreviewModal
          title={`${t('purchaseOrder.purchaseOrderDetails')} — ${printOrder.purchaseOrderNumber}`}
          onClose={() => setPrintOrder(null)}
          pdfFileName={`Purchase_Order_${printOrder.purchaseOrderNumber || printOrder.id}.pdf`}
          onDownloadPdf={() => exportPurchaseOrderPDF(printOrder, {
            primary: getCssVar('--color-primary', '#4f46e5'),
            primaryDark: getCssVar('--color-primary-dark', '#3730a3'),
          })}
          renderA4={() => <PurchaseOrderA4Document purchaseOrder={printOrder} />}
        />
      )}

      {detailsOrder && (
        <PurchaseOrderDetailsModal
          purchaseOrder={detailsOrder}
          statusInfo={statusConfig[detailsOrder.status]}
          onClose={() => setDetailsOrder(null)}
        />
      )}

      {paymentTarget && (
        <PurchaseOrderPaymentRecordsModal
          purchaseOrder={paymentTarget}
          onClose={() => setPaymentTarget(null)}
          onSaved={loadPurchaseOrders}
        />
      )}
    </div>
  );
};

export default PurchaseOrderListPage;
